<template lang="pug">
.organiser-wizard
  .wizard-progress.mb-4
    .wizard-progress__item(
      v-for="step in progressSteps"
      :key="step.key"
      :class="[progressClass(step.key), { 'wizard-progress__item--clickable': canNavigateTo(step.key) }]"
      :tabindex="canNavigateTo(step.key) ? 0 : -1"
      :role="canNavigateTo(step.key) ? 'button' : 'presentation'"
      @click="goToStep(step.key)"
      @keydown.enter.prevent="goToStep(step.key)"
      @keydown.space.prevent="goToStep(step.key)"
    )
      span.wizard-progress__index {{ step.index }}
      div
        p.wizard-progress__title.mb-0 {{ step.title }}
        small.text-muted {{ step.subtitle }}

  .alert.alert-info.mb-3(v-if="existingUser && !hasCompletionSummary")
    strong Вы уже вошли.
    |  Мы используем ваш текущий профиль и просто попросим указать детали события и компании.
  .alert.alert-danger(v-if="generalError") {{ generalError }}
  .alert.alert-danger(v-if="apiBaseError") {{ apiBaseError }}

  form(v-if="currentStep === 'account'" @submit.prevent="submitStep")
    h3.mb-3 Аккаунт организатора
    .form-row
      .form-group.col-md-6
        label Фамилия
        input.form-control(v-model="form.account.name_last" type="text" placeholder="Иванов" required)
        field-errors(:messages="fieldErrors('name_last')")
      .form-group.col-md-6
        label Имя
        input.form-control(v-model="form.account.name_first" type="text" placeholder="Иван" required)
        field-errors(:messages="fieldErrors('name_first')")
    .form-row
      .form-group.col-md-6
        birth-date-input(
          v-model="form.account.birth_date"
          label="Дата рождения"
          :required="true"
          :state="fieldErrors('birth_date').length ? false : null"
        )
        field-errors(:messages="fieldErrors('birth_date')")
      .form-group.col-md-6
        label Пол
        .d-flex.align-items-center.mt-2
          .custom-control.custom-radio.mr-3
            input#gender-male.custom-control-input(v-model="form.account.gender" type="radio" value="male" required)
            label.custom-control-label(for="gender-male") Мужской
          .custom-control.custom-radio
            input#gender-female.custom-control-input(v-model="form.account.gender" type="radio" value="female" required)
            label.custom-control-label(for="gender-female") Женский
        field-errors(:messages="fieldErrors('gender')")
    .form-row
      .form-group.col-md-6
        label Email
        input.form-control(v-model="form.account.email" type="email" placeholder="organiser@example.com" required)
        field-errors(:messages="fieldErrors('email')")
      .form-group.col-md-6
        label Телефон
        input.form-control(v-model="form.account.phone" type="tel" placeholder="+7 900 000-00-00" required)
        field-errors(:messages="fieldErrors('phone')")
    .form-row
      .form-group.col-md-6
        label Пароль
        input.form-control(v-model="form.account.password" type="password" autocomplete="new-password" required)
        field-errors(:messages="fieldErrors('password')")
      .form-group.col-md-6
        label Подтверждение пароля
        input.form-control(v-model="form.account.password_confirmation" type="password" autocomplete="new-password" required)
        field-errors(:messages="fieldErrors('password_confirmation')")
    wizard-controls(:loading="loading" :can-back="false" :submit-label="nextLabel")

  form(v-else-if="currentStep === 'event'" @submit.prevent="submitStep")
    h3.mb-3 Расскажите о мероприятии
    .form-group
      label Название
      input.form-control(v-model="form.event.name" type="text" placeholder="Забег 'Большой город'" required)
      field-errors(:messages="fieldErrors('name')")
    .form-group
      label Короткое описание
      textarea.form-control(v-model="form.event.lead" rows="2" placeholder="Почему участники полюбят именно этот старт" required)
      field-errors(:messages="fieldErrors('lead')")
    .form-group
      label Дата проведения
      input.form-control(v-model="form.event.start_date" type="date" required)
      field-errors(:messages="fieldErrors('start_date')")
    wizard-controls(:loading="loading" :can-back="true" :submit-label="nextLabel" @back="goToPrevious")

  form(v-else-if="currentStep === 'registration'" @submit.prevent="submitStep")
    h3.mb-3 Настройте регистрацию
    .form-row
      .form-group.col-md-6
        label Основная дисциплина
        input.form-control(v-model="form.registration.race_name" type="text" placeholder="5 км" required)
        field-errors(:messages="fieldErrors('race_name')")
      .form-group.col-md-6
        label Вид спорта
        select.form-control(v-model="form.registration.sport_id" required)
          option(disabled value="") Выберите из списка
          option(v-for="sport in sports" :key="sport.id" :value="sport.id") {{ sport.name }}
        field-errors(:messages="fieldErrors('sport_id')")
        small.text-muted(v-if="!sports.length") Список видов спорта пуст — обратитесь к поддержке.
        small.text-muted(v-else) Если нужного нет или мерпориятие не спортивное — выберите "Другой вид"
    .form-group
      .custom-control.custom-checkbox
        input#freeParticipation.custom-control-input(
          v-model="form.registration.free_participation"
          type="checkbox"
          @change="handleFreeParticipationToggle"
        )
        label.custom-control-label(for="freeParticipation") Бесплатное участие
      small.text-muted(v-if="form.registration.free_participation") Оплату можно подключить позже — сейчас достаточно просто открыть регистрацию.
    .form-row(v-if="!form.registration.free_participation")
      .form-group.col-md-6
        label Стоимость участия (₽)
        input.form-control(v-model="form.registration.price_rub" type="number" min="0" step="50" required)
        field-errors(:messages="fieldErrors('price_rub')")
    .form-row
      .form-group.col-md-6
        label Регистрация открыта с
        input.form-control(v-model="form.registration.registration_start" type="datetime-local" required)
        field-errors(:messages="fieldErrors('registration_start')")
      .form-group.col-md-6
        label Регистрация закрывается
        input.form-control(v-model="form.registration.registration_end" type="datetime-local" required)
        field-errors(:messages="fieldErrors('registration_end')")
    wizard-controls(:loading="loading" :can-back="true" :submit-label="nextLabel" @back="goToPrevious")

  form(v-else-if="currentStep === 'company'" @submit.prevent="submitStep")
    h3.mb-3 Компания организатора
    p.text-muted(v-if="form.registration.free_participation") Для бесплатных мероприятий этот шаг можно пропустить и настроить реквизиты позже.
    .form-group
      .custom-control.custom-checkbox
        input#companyPhysical.custom-control-input(
          v-model="form.company.physical"
          type="checkbox"
        )
        label.custom-control-label(for="companyPhysical") Я оформляю как физическое лицо
      small.text-muted(v-if="form.company.physical") Мы попросим только ИНН — остальные данные можно добавить в кабинете.
      small.text-muted(v-else) Для юрлиц и ИП заполним реквизиты автоматически через подсказки.
    template(v-if="form.company.physical")
      .form-group
        label ИНН
        input.form-control(v-model="form.company.inn" type="text" placeholder="123456789012" required)
        field-errors(:messages="fieldErrors('inn')")
    template(v-else)
      .form-group
        label Найдите компанию по названию или ИНН
        .input-group
          input.form-control(
            v-model="dadataQuery"
            type="text"
            placeholder="ООО Ромашка или 7707083893"
            @keyup.enter.prevent="searchCompany"
          )
          .input-group-append
            button.btn.btn-outline-secondary(type="button" :disabled="dadataLoading" @click.prevent="searchCompany")
              | {{ dadataLoading ? 'Поиск…' : 'Найти' }}
        //small.form-text.text-muted Мы используем DaData, чтобы подсказать реквизиты и адрес.
        small.text-danger.d-block.mt-1(v-if="dadataError") {{ dadataError }}
        .list-group.mt-2(v-if="dadataSuggestions.length")
          button.list-group-item.list-group-item-action(
            v-for="(suggestion, index) in dadataSuggestions"
            :key="index"
            type="button"
            @click="applyCompanySuggestion(suggestion)"
          )
            strong {{ suggestion.title }}
            small.d-block.text-muted {{ suggestionSubtitle(suggestion) }}
      .form-group
        label Название на площадке
        input.form-control(v-model="form.company.brand_name" type="text" placeholder="Бренд, который увидят участники" required)
        field-errors(:messages="fieldErrors('brand_name')")
      .form-group
        label Юридическое название
        input.form-control(v-model="form.company.official_name" type="text" placeholder="ООО «Ромашка СПОРТ»" required)
        field-errors(:messages="fieldErrors('official_name')")
      .form-group
        label Адрес или город регистрации
        input.form-control(v-model="form.company.address" type="text" placeholder="Москва, ул. Пушкина 10" required)
        field-errors(:messages="fieldErrors('address')")
      .form-group
        label Контактный телефон для участников
        input.form-control(v-model="form.company.contact_phone" type="tel" placeholder="+7 900 000-00-00")
        field-errors(:messages="fieldErrors('phones')")
    wizard-controls(:loading="loading" :can-back="true" :submit-label="nextLabel" @back="goToPrevious")

  form(v-else-if="currentStep === 'confirm'" @submit.prevent="submitStep")
    h3.mb-3 Проверка и запуск
    p.text-muted Мы создадим аккаунт организатора, компанию и черновик события. После завершения вы попадёте в кабинет для финальных настроек.
    .summary.mb-4
      div
        strong Аккаунт:
        p.mb-1 {{ form.account.name_last }} {{ form.account.name_first }}
        p.mb-1 {{ form.account.email }} · {{ form.account.phone }}
      div(v-if="!shouldSkipCompanyStep() || form.company.brand_name || form.company.official_name")
        strong Компания:
        template(v-if="form.company.physical")
          p.mb-1 Физическое лицо
          p.mb-1(v-if="form.company.inn") ИНН {{ form.company.inn }}
        template(v-else)
          p.mb-1 {{ form.company.brand_name }}
          p.mb-1 {{ form.company.official_name }}
          p.mb-1(v-if="form.company.address") {{ form.company.address }}
      div
        strong Событие:
        p.mb-1 {{ form.event.name }}
        p.mb-1(v-if="formattedDateRange") {{ formattedDateRange }}
      div
        strong Регистрация:
        p.mb-1
          | {{ form.registration.race_name }}
          span(v-if="form.registration.distance_km") · {{ form.registration.distance_km }} км
        p.mb-1(v-if="form.registration.capacity") {{ form.registration.capacity }} мест
        p.mb-1(v-if="form.registration.free_participation") Бесплатное участие
        p.mb-1(v-else) {{ form.registration.price_rub }} ₽
    wizard-controls(:loading="loading" :can-back="true" submit-label="Запустить регистрацию" @back="goToPrevious")

  section(v-else-if="currentStep === 'success'" class="happy-page")
    h3.mb-3 Поздравляем — мероприятие создано!
    p.text-muted.mb-4 Теперь вы можете продолжить настройку в кабинете REGPLACE.
    h4.mb-3 Что сделать дальше
    ul.list-unstyled.recommendations
      li(v-for="(block, idx) in successRecommendations" :key="idx" class="recommendations__item")
        h5.mb-1 {{ block.title }}
        p.text-muted.small.mb-2 {{ block.description }}
        a(:href="block.url" target="_blank" rel="noopener" class="small") Перейти →
    .success-actions.d-flex.flex-column.flex-md-row
      a.btn.btn-dark.mb-2.mb-md-0(:href="completionSummary.admin_url" target="_blank" rel="noopener") Перейти в кабинет
</template>

<script>
import BirthDateInput from '../birth-date-input.vue';

const STORAGE_KEY = 'organiser-onboarding';

const WizardControls = {
  props: {
    loading: { type: Boolean, default: false },
    canBack: { type: Boolean, default: false },
    submitLabel: { type: String, default: 'Далее' }
  },
  render(h) {
    return h('div', { class: 'd-flex justify-content-between align-items-center' }, [
      this.canBack
        ? h(
            'button',
            {
              class: 'btn btn-link px-0',
              attrs: { type: 'button' },
              on: { click: () => this.$emit('back') }
            },
            'Назад'
          )
        : h('span'),
      h(
        'button',
        {
          class: 'btn btn-dark',
          attrs: { type: 'submit', disabled: this.loading }
        },
        this.loading ? 'Сохраняем…' : this.submitLabel
      )
    ]);
  }
};

const FieldErrors = {
  props: {
    messages: { type: Array, default: () => [] }
  },
  render(h) {
    if (!this.messages.length) return null;
    return h('small', { class: 'text-danger d-block mt-1' }, this.messages.join(', '));
  }
};

export default {
  name: 'OrganiserWizard',
  components: {
    BirthDateInput,
    WizardControls,
    FieldErrors
  },
  props: {
    state: { type: Object, default: () => ({}) },
    sports: { type: Array, default: () => [] },
    existingUser: { type: Boolean, default: false }
  },
  data() {
    const wizardState = this.state || {};
    return {
      loading: false,
      errors: {},
      apiBaseError: null,
      generalError: null,
      wizardState,
      currentStep: this.detectInitialStep(wizardState),
      form: this.composeInitialForm(wizardState),
      dadataQuery: '',
      dadataSuggestions: [],
      dadataLoading: false,
      dadataError: null,
      completionSummary: null,
      copySuccess: false,
      pageViewGoalTracked: false,
      registrationStepGoalTracked: false,
      successGoalTracked: false
    };
  },
  created() {
    if (this.form.company && this.form.company.physical) {
      this.handleCompanyTypeChange();
    }
  },
  mounted() {
    this.trackPageViewGoal();
    this.trackVisibleStepGoals(this.currentStep);
  },
  computed: {
    nextLabel() {
      if (this.currentStep === 'registration') return this.shouldSkipCompanyStep() ? 'К подтверждению' : 'К компании';
      if (this.currentStep === 'company') return 'К подтверждению';
      if (this.currentStep === 'confirm') return 'Запустить регистрацию';
      return 'Далее';
    },
    progressSteps() {
      const steps = [
        { key: 'account', title: 'Аккаунт', subtitle: 'организатор', index: 1 },
        { key: 'event', title: 'Мероприятие', subtitle: 'дата и описание', index: 2 },
        { key: 'registration', title: 'Детали', subtitle: 'дисциплина, лимиты, цена', index: 3 },
        { key: 'company', title: 'Компания', subtitle: 'ООО, ИП или физлицо', index: 4 },
        { key: 'confirm', title: 'Проверка', subtitle: 'чек-лист', index: 5 }
      ];

      if (this.hasCompletionSummary) {
        steps.push({ key: 'success', title: 'Готово', subtitle: 'что дальше', index: 6 });
      }

      return steps;
    },
    formattedDateRange() {
      return this.form.event.start_date || '';
    },
    hasCompletionSummary() {
      return Boolean(this.completionSummary);
    },
    successRecommendations() {
      if (!this.completionSummary || !Array.isArray(this.completionSummary.recommendations)) return [];
      return this.completionSummary.recommendations;
    }
  },
  watch: {
    form: {
      handler() {
        this.persistLocally();
      },
      deep: true
    },
    'form.company.physical'() {
      this.handleCompanyTypeChange();
    },
    currentStep(newStep) {
      this.trackVisibleStepGoals(newStep);
    }
  },
  methods: {
    trackMetrikaGoal(goal) {
      try {
        if (typeof window !== 'undefined' && typeof window.ym === 'function') {
          window.ym(33634359, 'reachGoal', goal);
        }
      } catch (error) {
        // Ignore metrika failures
      }
    },
    trackPageViewGoal() {
      if (this.pageViewGoalTracked) return;

      this.pageViewGoalTracked = true;
      this.trackMetrikaGoal('organiser_register_page_view');
    },
    trackVisibleStepGoals(visibleStep) {
      if (!this.registrationStepGoalTracked && visibleStep === 'registration') {
        this.registrationStepGoalTracked = true;
        this.trackMetrikaGoal('2_step');
      }

      if (!this.successGoalTracked && visibleStep === 'success' && this.hasCompletionSummary) {
        this.successGoalTracked = true;
        this.trackMetrikaGoal('organiser_register_success');
      }
    },
    composeInitialForm(wizardState = this.wizardState || {}) {
      const defaults = {
        account: {
          email: '',
          phone: '',
          password: '',
          password_confirmation: '',
          name_first: '',
          name_last: '',
          birth_date: '',
          gender: ''
        },
        event: {
          name: '',
          lead: '',
          naming: '',
          start_date: ''
        },
        registration: {
          race_name: '',
          distance_km: '',
          price_rub: '',
          capacity: '',
          registration_start: '',
          registration_end: '',
          sport_id: '',
          free_participation: false
        },
        company: {
          brand_name: '',
          official_name: '',
          address: '',
          contact_phone: '',
          dadata_payload: null,
          inn: '',
          physical: false
        }
      };
      const stateForm = Object.assign({}, defaults, wizardState || {});
      const restored = this.restoreFromStorage();
      return {
        account: Object.assign({}, defaults.account, stateForm.account, restored.account || {}),
        event: Object.assign({}, defaults.event, stateForm.event, restored.event || {}),
        registration: Object.assign({}, defaults.registration, stateForm.registration, restored.registration || {}),
        company: Object.assign({}, defaults.company, stateForm.company, restored.company || {})
      };
    },
    detectInitialStep(wizardState = this.wizardState || {}) {
      if (!wizardState || !Object.keys(wizardState).length) return 'account';
      if (!wizardState.event) return 'event';
      if (!wizardState.registration) return 'registration';
      const skipCompany = this.shouldSkipCompanyFromState(wizardState);
      if (!wizardState.company && skipCompany) return 'confirm';
      if (!wizardState.company) return 'company';
      return 'confirm';
    },
    progressClass(stepKey) {
      const order = this.progressSteps.map((step) => step.key);
      const currentIndex = order.indexOf(this.currentStep);
      const stepIndex = order.indexOf(stepKey);
      if (stepIndex < currentIndex) return 'wizard-progress__item--done';
      if (stepIndex === currentIndex) return 'wizard-progress__item--current';
      return '';
    },
    canNavigateTo(stepKey) {
      if (!stepKey || stepKey === this.currentStep) return false;
      if (stepKey === 'company' && this.shouldSkipCompanyStep()) return false;
       if (stepKey === 'success' && !this.hasCompletionSummary) return false;
      const order = this.progressSteps.map((step) => step.key);
      const currentIndex = order.indexOf(this.currentStep);
      const stepIndex = order.indexOf(stepKey);
      return stepIndex > -1 && stepIndex < currentIndex;
    },
    fieldErrors(field) {
      return this.errors[field] || [];
    },
    goToPrevious() {
      const order = this.progressSteps.map((step) => step.key);
      let idx = order.indexOf(this.currentStep);
      if (idx > 0) {
        do {
          idx -= 1;
        } while (idx >= 0 && order[idx] === 'company' && this.shouldSkipCompanyStep());
        if (idx >= 0) {
          this.currentStep = order[idx];
          this.errors = {};
          this.apiBaseError = null;
        }
      }
    },
    goToStep(stepKey) {
      if (!this.canNavigateTo(stepKey)) return;
      this.currentStep = stepKey;
      this.errors = {};
      this.apiBaseError = null;
      this.generalError = null;
    },
    submitStep() {
      this.generalError = null;
      this.apiBaseError = null;
      this.errors = {};
      const endpoint = this.endpointFor(this.currentStep);
      if (!endpoint) return;

      this.loading = true;
      this.axios
        .post(endpoint, this.payloadForCurrentStep())
        .then((response) => {
          this.loading = false;
          if (this.currentStep === 'confirm') {
            this.completionSummary = response.data.summary || null;
            this.resetLocalSnapshot();
            this.currentStep = 'success';
            return;
          }
          this.hydrateState(response.data.state);
          this.advanceStep();
        })
        .catch((error) => {
          this.loading = false;
          if (error.response && error.response.data) {
            this.errors = this.normalizeErrors(error.response.data.errors || {});
            if (this.errors.base) {
              this.apiBaseError = this.errors.base.join(', ');
              delete this.errors.base;
            } else {
              this.apiBaseError = null;
            }
          } else {
            this.generalError = 'Ошибка сети. Попробуйте ещё раз.';
          }
        });
    },
    payloadForCurrentStep() {
      if (this.currentStep === 'account') return { account: this.form.account };
      if (this.currentStep === 'event') return { event: this.form.event };
      if (this.currentStep === 'registration') return { registration: this.form.registration };
      if (this.currentStep === 'company') return { company: this.form.company };
      return {};
    },
    advanceStep() {
      const order = this.progressSteps.map((step) => step.key);
      let idx = order.indexOf(this.currentStep);
      while (idx < order.length - 1) {
        idx += 1;
        if (order[idx] === 'company' && this.shouldSkipCompanyStep()) {
          continue;
        }
        this.currentStep = order[idx];
        break;
      }
    },
    hydrateState(newState) {
      if (!newState) return;
      this.wizardState = newState;
      if (newState.account) this.form.account = Object.assign({}, this.form.account, newState.account);
      if (newState.event) this.form.event = Object.assign({}, this.form.event, newState.event);
      if (newState.registration) this.form.registration = Object.assign({}, this.form.registration, newState.registration);
      if (newState.company) this.form.company = Object.assign({}, this.form.company, newState.company);
      this.persistLocally();
    },
    endpointFor(step) {
      switch (step) {
        case 'account':
          return '/register-org/steps/account';
        case 'event':
          return '/register-org/steps/event';
        case 'registration':
          return '/register-org/steps/registration';
        case 'company':
          return '/register-org/steps/company';
        case 'confirm':
          return '/register-org/complete';
        default:
          return null;
      }
    },
    persistLocally() {
      try {
        const snapshot = JSON.parse(JSON.stringify(this.form));
        if (snapshot.account) {
          snapshot.account.password = '';
          snapshot.account.password_confirmation = '';
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
      } catch (error) {
        // Ignore quota/storage errors
      }
    },
    restoreFromStorage() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        if (parsed.account) {
          parsed.account.password = '';
          parsed.account.password_confirmation = '';
        }
        return parsed;
      } catch (error) {
        return {};
      }
    },
    resetLocalSnapshot() {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        // ignore
      }
    },
    normalizeErrors(rawErrors) {
      return Object.entries(rawErrors).reduce((acc, [key, value]) => {
        acc[key] = [].concat(value || []).filter((message) => message);
        return acc;
      }, {});
    },
    searchCompany() {
      if (!this.dadataQuery.trim()) {
        this.dadataSuggestions = [];
        this.dadataError = null;
        return;
      }
      this.dadataLoading = true;
      this.dadataError = null;
      this.axios
        .get('/register-org/company/suggest', { params: { query: this.dadataQuery.trim() } })
        .then(({ data }) => {
          this.dadataLoading = false;
          this.dadataSuggestions = data.suggestions || [];
          this.dadataError = data.error || null;
        })
        .catch(() => {
          this.dadataLoading = false;
          this.dadataSuggestions = [];
          this.dadataError = 'Не удалось получить подсказки. Попробуйте позже.';
        });
    },
    applyCompanySuggestion(suggestion) {
      this.form.company.brand_name = suggestion.brand_name || suggestion.title;
      this.form.company.official_name = suggestion.official_name || suggestion.title;
      this.form.company.address = suggestion.address || '';
      this.form.company.dadata_payload = suggestion.payload || null;
      this.dadataSuggestions = [];
      this.dadataQuery = suggestion.title;
    },
    suggestionSubtitle(suggestion) {
      if (suggestion.inn && suggestion.address) {
        return `${suggestion.inn} · ${suggestion.address}`;
      }
      if (suggestion.inn) return suggestion.inn;
      return suggestion.address || '';
    },
    handleFreeParticipationToggle() {
      if (this.shouldSkipCompanyStep()) {
        this.form.registration.price_rub = '';
      }
    },
    handleCompanyTypeChange() {
      if (!this.form.company) return;
      if (this.form.company.physical) {
        this.form.company.brand_name = '';
        this.form.company.official_name = '';
        this.form.company.address = '';
        this.form.company.contact_phone = '';
        this.form.company.dadata_payload = null;
        this.dadataQuery = '';
        this.dadataSuggestions = [];
        this.dadataError = null;
      } else {
        this.form.company.inn = '';
      }
    },
    shouldSkipCompanyStep() {
      const value = this.form.registration.free_participation;
      return value === true || value === 'true' || value === 1;
    },
    shouldSkipCompanyFromState(state = this.wizardState || {}) {
      if (!state || !state.registration) return false;
      const value = state.registration.free_participation;
      return value === true || value === 'true' || value === 1;
    },
    copyEventLink() {
      if (!this.completionSummary) return;
      const text = this.completionSummary.event_url;
      if (!text) return;

      const write = (value) => {
        this.copySuccess = true;
        setTimeout(() => (this.copySuccess = false), 2000);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(write).catch(() => this.fallbackCopy(text, write));
      } else {
        this.fallbackCopy(text, write);
      }
    },
    fallbackCopy(text, cb) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        cb(text);
      } catch (error) {
        // noop
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }
};
</script>

<style scoped>
.organiser-wizard {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 25px 65px rgba(15, 23, 42, 0.15);
}

@media (max-width: 768px) {
  .organiser-wizard {
    padding: 16px;
  }
}

.wizard-progress {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.wizard-progress__item {
  background: #f8f9fb;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.9rem;
}

@media (max-width: 576px) {
  .wizard-progress__item {
    flex-direction: column;
    align-items: flex-start;
  }
}

.wizard-progress__item--clickable {
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.wizard-progress__item--clickable:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(60, 64, 198, 0.35);
}

.wizard-progress__item--clickable:hover {
  transform: translateY(-1px);
}

.wizard-progress__item--current {
  border: 1px solid #3c40c6;
  background: #eef0ff;
}

.wizard-progress__item--done {
  border: 1px solid #38c172;
  background: #edfdf5;
}

.wizard-progress__index {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.3rem;
}

.summary {
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

@media (max-width: 576px) {
  .summary {
    grid-template-columns: 1fr;
  }
}

.happy-page {
  text-align: left;
}

.success-card {
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
}

.success-card p.text-monospace {
  font-size: 0.95rem;
  word-break: break-all;
}

.recommendations__item {
  border-left: 3px solid #eef0ff;
  padding: 12px 0 12px 16px;
}

.recommendations__item + .recommendations__item {
  margin-top: 8px;
}

.success-actions .btn + .btn {
  margin-left: 0;
}

@media (min-width: 768px) {
  .success-actions {
    align-items: center;
  }
  .success-actions .btn {
    width: auto;
  }
  .success-actions .btn + .btn {
    margin-left: 12px;
  }
}

@media (max-width: 767px) {
  .success-actions .btn {
    width: 100%;
  }
}
</style>
